import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useThemeClasses, cn } from '../../hooks/useThemeClasses';
import StepIndicator from './StepIndicator';
import SectionHeading from './SectionHeading';
import { WorkflowModalWithContext, WorkflowModalProps } from './WorkflowModal';
import { ModalFooter, FooterButton } from './ModalFooter';

/**
 * Step definition interface
 */
export interface Step {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  enabled: boolean;
  hidden?: boolean;
}

/**
 * Multi-step modal props interface
 */
export interface MultiStepModalProps extends Omit<WorkflowModalProps, 'children'> {
  /** Array of step definitions */
  steps: Step[];
  /** Current active step ID */
  currentStep: number;
  /** Initial step ID (defaults to first step) */
  initialStep?: number;
  /** Callback fired when step changes */
  onStepChange?: (stepId: number) => void;
  /** Callback fired when step is completed */
  onStepComplete?: (stepId: number) => void;
  /** Function to render content for each step */
  renderStep: (stepId: number, stepData: Step) => React.ReactNode;
  /** Function to get step title dynamically */
  getStepTitle?: (stepId: number, stepData: Step) => string;
  /** Whether to show step indicator */
  showStepIndicator?: boolean;
  /** Whether to allow clicking on enabled steps */
  allowStepNavigation?: boolean;
  /** Whether to scroll to top when step changes */
  scrollToTopOnStepChange?: boolean;
  /** Custom header content for final step */
  finalStepHeader?: React.ReactNode;
  /** Whether the final step should show success styling */
  showSuccessHeader?: boolean;
  /** Function to get footer buttons for each step */
  getFooterButtons?: (stepId: number, stepData: Step) => FooterButton[];
  /** Whether to show footer */
  showFooter?: boolean;
  /** Footer alignment */
  footerAlignment?: 'left' | 'center' | 'right' | 'between' | 'around';
  /** Function to get custom content to display above footer buttons */
  getCustomFooterContent?: (stepId: number, stepData: Step) => React.ReactNode;
}

/**
 * MultiStepModal - Reusable component for step-based modal workflows
 * 
 * Features:
 * - Step progress tracking and navigation
 * - Automatic state management
 * - Theme integration
 * - Workflow integration via WorkflowModal
 * - Customizable step rendering
 * - Success step styling
 */
export const MultiStepModal: React.FC<MultiStepModalProps> = ({
  steps: initialSteps,
  currentStep,
  initialStep,
  onStepChange,
  onStepComplete,
  renderStep,
  getStepTitle,
  showStepIndicator = true,
  allowStepNavigation = true,
  scrollToTopOnStepChange = true,
  finalStepHeader,
  showSuccessHeader = true,
  getFooterButtons,
  showFooter = true,
  footerAlignment = 'between',
  getCustomFooterContent,
  isOpen,
  onClose,
  hotelId,
  workflowSessionId,
  conversationId,
  userEmail,
  agentName,
  onComplete,
  title,
  fullSize = false,
  maxWidth = '4xl',
  className,
  showCloseButton = true,
  autoExitWorkflow = false,
}) => {
  const tc = useThemeClasses();
  
  // State for steps (local copy that can be modified)
  const [steps, setSteps] = useState<Step[]>(initialSteps);
  
  // Store scroll function from Modal component
  const [scrollToTop, setScrollToTop] = useState<(() => void) | null>(null);
  
  // Debug: Log when scroll function is received
  const handleContentRefReady = useCallback((scrollFn: () => void) => {
    console.log('🔝 MultiStepModal - Received scroll function from Modal:', scrollFn);
    setScrollToTop(() => scrollFn);
  }, []);
  
  // Determine if current step is the final step
  const finalStepId = useMemo(() => {
    // Get the highest step ID, regardless of visibility (more robust for hidden final steps)
    return Math.max(...steps.map(step => step.id));
  }, [steps]);
  
  const isFinalStep = currentStep === finalStepId;
  const currentStepData = steps.find(step => step.id === currentStep);


  /**
   * Reset modal state when it opens
   */
  useEffect(() => {
    if (isOpen) {
      setSteps(initialSteps);
      if (initialStep && onStepChange) {
        onStepChange(initialStep);
      }
    }
  }, [isOpen, initialSteps, initialStep, onStepChange]);

  /**
   * Update steps when initialSteps changes (even when modal is open)
   */
  useEffect(() => {
    setSteps(initialSteps);
  }, [initialSteps]);

  /**
   * Handle scroll to top when current step changes
   */
  useEffect(() => {
    if (!isOpen || !scrollToTopOnStepChange) return;
    
    console.log('🔝 MultiStepModal - Step changed to:', currentStep, 'scrollToTop function:', scrollToTop);
    
    // Add delay to ensure content is fully rendered before scrolling
    const scrollDelay = setTimeout(() => {
      if (scrollToTop) {
        console.log('🔝 MultiStepModal - Using Modal scroll function due to step change');
        scrollToTop();
        
        // Additional delay to ensure scroll completes before any focus events
        setTimeout(() => {
          console.log('🔝 MultiStepModal - Scroll completed, preventing focus scroll');
        }, 150);
      } else {
        console.log('🔝 MultiStepModal - Scroll function not available, using fallback due to step change');
        const modalContent = document.querySelector('[data-modal-content]');
        console.log('🔝 MultiStepModal - Found modal content element:', modalContent);
        
        if (modalContent) {
          console.log('🔝 MultiStepModal - Using fallback querySelector approach due to step change');
          modalContent.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          console.log('🔝 MultiStepModal - No modal container found, scrolling window due to step change');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 150); // Increased delay to ensure content rendering is complete

    return () => clearTimeout(scrollDelay);
  }, [currentStep, isOpen, scrollToTopOnStepChange, scrollToTop]);

  /**
   * Handle step navigation clicks
   */
  const handleStepClick = useCallback((stepId: number) => {
    if (!allowStepNavigation) return;
    
    const step = steps.find(s => s.id === stepId);
    if (step?.enabled) {
      onStepChange?.(stepId);
      
      if (scrollToTopOnStepChange) {
        console.log('🔝 MultiStepModal - Scroll requested, scrollToTop function:', scrollToTop);
        
        // Add delay to ensure content is fully rendered before scrolling
        setTimeout(() => {
          if (scrollToTop) {
            console.log('🔝 MultiStepModal - Using Modal scroll function');
            scrollToTop();
          } else {
            // Fallback to querySelector approach if ref-based scroll not available yet
            console.log('🔝 MultiStepModal - Scroll function not available, using fallback');
            const modalContent = document.querySelector('[data-modal-content]');
            console.log('🔝 MultiStepModal - Found modal content element:', modalContent);
            
            if (modalContent) {
              console.log('🔝 MultiStepModal - Using fallback querySelector approach');
              modalContent.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              console.log('🔝 MultiStepModal - No modal container found, scrolling window');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }
        }, 150); // Consistent delay with useEffect
      }
    }
  }, [steps, allowStepNavigation, onStepChange, scrollToTopOnStepChange, scrollToTop]);

  /**
   * Update step progress
   */
  const updateStepProgress = useCallback((
    stepId: number, 
    completed: boolean = true, 
    enableNext: boolean = true
  ) => {
    setSteps(prevSteps => prevSteps.map(step => {
      if (step.id === stepId) {
        return { ...step, completed };
      }
      if (step.id === stepId + 1 && enableNext) {
        return { ...step, enabled: true };
      }
      return step;
    }));

    if (completed) {
      onStepComplete?.(stepId);
    }
  }, [onStepComplete]);

  /**
   * Get current step title
   */
  const getCurrentStepTitle = useCallback(() => {
    if (!currentStepData) return title || '';
    
    if (getStepTitle) {
      return getStepTitle(currentStep, currentStepData);
    }
    
    return currentStepData.title;
  }, [currentStep, currentStepData, getStepTitle, title]);

  /**
   * Render modal header
   */
  const renderHeader = useCallback(() => {
    // Always render the standard header with step indicator when showStepIndicator is true
    // Only use custom final step headers when explicitly provided
    if (isFinalStep && finalStepHeader) {
      return finalStepHeader;
    }

    return (
      <div className="w-full">
        {title && (
          <SectionHeading as="h2" showRule={true} variant="large">
            {title}
          </SectionHeading>
        )}
        {showStepIndicator && (
          <div>
            <StepIndicator
              steps={steps.filter(step => !step.hidden)}
              currentStep={currentStep}
              onStepClick={allowStepNavigation ? handleStepClick : undefined}
            />
          </div>
        )}
      </div>
    );
  }, [
    isFinalStep,
    finalStepHeader,
    showSuccessHeader,
    getCurrentStepTitle,
    tc,
    title,
    showStepIndicator,
    steps,
    currentStep,
    allowStepNavigation,
    handleStepClick
  ]);

  /**
   * Get footer buttons for current step
   */
  const footerButtons = useMemo(() => {
    if (!showFooter || !getFooterButtons || !currentStepData) {
      return [];
    }
    return getFooterButtons(currentStep, currentStepData);
  }, [showFooter, getFooterButtons, currentStep, currentStepData]);

  /**
   * Provide context for child components
   */
  const stepContext = useMemo(() => ({
    currentStep,
    steps,
    updateStepProgress,
    isFinalStep,
    currentStepData,
    footerButtons,
  }), [currentStep, steps, updateStepProgress, isFinalStep, currentStepData, footerButtons]);

  return (
    <WorkflowModalWithContext
      isOpen={isOpen}
      onClose={onClose}
      hotelId={hotelId}
      workflowSessionId={workflowSessionId}
      conversationId={conversationId}
      userEmail={userEmail}
      agentName={agentName}
      onComplete={onComplete}
      title={undefined}
      customHeader={renderHeader()}
      fullSize={fullSize}
      maxWidth={maxWidth}
      className={className}
      showCloseButton={showCloseButton}
      autoExitWorkflow={autoExitWorkflow}
      onContentRefReady={handleContentRefReady}
      footer={showFooter && footerButtons.length > 0 ? (
        <>
          {getCustomFooterContent?.(currentStep, currentStepData)}
          <ModalFooter
            buttons={footerButtons}
            alignment={footerAlignment}
            showBorder={false}
            sticky={false}
          />
        </>
      ) : undefined}
    >
      <MultiStepProvider value={stepContext}>
        {/* Content only - header and footer are now handled by Modal component */}
        {currentStepData && renderStep(currentStep, currentStepData)}
      </MultiStepProvider>
    </WorkflowModalWithContext>
  );
};

/**
 * Multi-step context interface
 */
export interface MultiStepContextData {
  currentStep: number;
  steps: Step[];
  updateStepProgress: (stepId: number, completed?: boolean, enableNext?: boolean) => void;
  isFinalStep: boolean;
  currentStepData?: Step;
  footerButtons: FooterButton[];
}

// Context for sharing multi-step data with child components
const MultiStepContext = React.createContext<MultiStepContextData | null>(null);

/**
 * MultiStepProvider - Provides multi-step context to child components
 */
export const MultiStepProvider: React.FC<{
  children: React.ReactNode;
  value: MultiStepContextData;
}> = ({ children, value }) => (
  <MultiStepContext.Provider value={value}>
    {children}
  </MultiStepContext.Provider>
);

/**
 * Hook to access multi-step context data
 */
export const useMultiStepContext = (): MultiStepContextData => {
  const context = React.useContext(MultiStepContext);
  if (!context) {
    throw new Error('useMultiStepContext must be used within a MultiStepProvider');
  }
  return context;
};

/**
 * Helper hook for common step operations
 */
export const useStepHelpers = () => {
  const { currentStep, updateStepProgress } = useMultiStepContext();

  const completeCurrentStep = useCallback((enableNext: boolean = true) => {
    updateStepProgress(currentStep, true, enableNext);
  }, [currentStep, updateStepProgress]);

  const enableNextStep = useCallback(() => {
    updateStepProgress(currentStep + 1, false, true);
  }, [currentStep, updateStepProgress]);

  return {
    completeCurrentStep,
    enableNextStep,
  };
};